import type { PlanTier } from "@/lib/authz/plans";
import { featureKeys, type FeatureKey } from "@/lib/authz/policy";

/**
 * Per-plan tool access, hand-copied from the real server-side entitlement
 * (`FEATURES_BY_TITLE` in src/lib/server/sealSubscriptions.ts, run through
 * `buildFeaturePermissions` in ./policy.ts — which is why e.g. `badgemaker`
 * and `addoneupcreator` appear: they piggyback on `itemimage` / `eupviewer`).
 * Client code cannot import the server module, so this snapshot exists for
 * DISPLAY ONLY: the admin "Preview as plan" control uses it to show which
 * tools a customer on a given plan would see unlocked. It never feeds a
 * server authorization decision.
 *
 * `planFeatures.test.ts` asserts each tier here deep-equals what the server
 * mapping produces, so drift fails loudly instead of showing a wrong preview.
 */
export type PaidPlanTier = Exclude<PlanTier, "free">;

const VEHICLE: readonly FeatureKey[] = [
  "paintbooth",
  "liverymagic",
  "uvstudio",
  "blinklab",
  "metaroom",
  "chevronbuilder",
  "creatorconvert",
  "handlingdiff",
  "assets",
];

const SERVER: readonly FeatureKey[] = [
  "packbench",
  "codesmith",
  "codevisualizer",
  "scriptdocs",
  "eupviewer",
  "addoneupcreator",
  "weaponviewer",
  "itemimage",
  "badgemaker",
  "embroiderytool",
  "tripoai",
  "metaroom",
  "creatorconvert",
  "resmon",
  "assets",
];

const PRO: readonly FeatureKey[] = Array.from(new Set([...VEHICLE, ...SERVER]));

export const PLAN_FEATURES: Record<PaidPlanTier, readonly FeatureKey[]> = {
  // creatorconvert is granted to any personal subscription.
  paintbooth: ["paintbooth", "creatorconvert"],
  vehicle_dev: VEHICLE,
  server_dev: SERVER,
  all_services: PRO,
  studio: [...PRO, "blinklab3d"],
};

/** Full permissions map for `tier`; `free` unlocks nothing. */
export function permissionsForPlan(tier: PlanTier): Record<FeatureKey, boolean> {
  const granted = new Set<FeatureKey>(tier === "free" ? [] : PLAN_FEATURES[tier]);
  return Object.fromEntries(
    featureKeys.map((key) => [key, granted.has(key)]),
  ) as Record<FeatureKey, boolean>;
}
